import {
  ActivityFilterSections,
  type ActivityFilters,
  type Practitioners,
} from "@/resources/types";

/**
 * Timeline item types that can be filtered
 */
export type TimelineItemType =
  | "simpleAndNote"
  | "communication"
  | "simple"
  | "create"
  | "status"
  | "allergy"
  | "location"
  | "tasks"
  | "triage"
  | "observation"
  | "comment"
  | "event"
  | "encounter"
  | "outbound"
  | "inbound";

/**
 * Base timeline item structure for filtering
 */
export type FilterableTimelineItem = {
  type: TimelineItemType;
  role: Practitioners | "other" | "System";
};

/**
 * Checks if an item matches the category filters
 * @param filters - Array of category filter strings
 * @param item - Timeline item to check
 * @returns true if the item matches any category filter
 */
export const matchesCategory = (
  filters: string[],
  item: FilterableTimelineItem
): boolean => {
  // If no category filters are applied, show everything
  if (!filters || filters.length === 0) return true;

  const itemType = item.type as string;

  return (
    (filters.includes("allergy") && itemType === "allergy") ||
    (filters.includes("task") && itemType === "tasks") ||
    (filters.includes("location") && itemType === "location") ||
    (filters.includes("triage") && itemType === "triage") ||
    (filters.includes("observation") && itemType === "observation") ||
    (filters.includes("communication") && itemType === "communication") ||
    (filters.includes("note") && itemType === "simpleAndNote") ||
    (filters.includes("comment") && itemType === "comment") ||
    (filters.includes("create") && itemType === "create")
  );
};

/**
 * Checks if an item matches the system category filters
 * @param filters - Array of system filter strings
 * @param item - Timeline item to check
 * @returns true if the item matches any system filter
 */
export const matchesSystemCategory = (
  filters: string[],
  item: FilterableTimelineItem
): boolean => {
  // If no system filters are applied, show everything
  if (!filters || filters.length === 0) return true;

  // If it's not an event type, it's not a system filter
  if (
    item.type !== "event" &&
    item.type !== "encounter" &&
    item.type !== "outbound" &&
    item.type !== "inbound"
  ) {
    return false;
  }

  // If it's a regular "event" type, no filter matches (return false)
  if (item.type === "event") {
    return false;
  }

  // Match based on event type
  if (item.type === "encounter") {
    return filters.includes("encounterEvents");
  }
  if (item.type === "outbound") {
    return filters.includes("outboundMessages");
  }
  if (item.type === "inbound") {
    return filters.includes("inboundMessages");
  }

  return false;
};

/**
 * Filters a timeline item based on the provided filters
 * Logic: (category OR system) AND input
 * - Input filter is AND - must match if specified
 * - Category and system filters are OR - match if either matches
 *
 * @param item - Timeline item to filter
 * @param filters - Activity filters to apply
 * @returns true if the item should be included, false otherwise
 */
export const filterTimelineItem = (
  item: FilterableTimelineItem,
  filters: ActivityFilters
): boolean | undefined => {
  const role = item.role as Practitioners | "other" | "System";

  // Input filter is AND - must match if specified
  const matchesInput =
    !filters[ActivityFilterSections.INPUT] ||
    filters[ActivityFilterSections.INPUT].length === 0 ||
    filters[ActivityFilterSections.INPUT].includes(role);

  // Category and system filters are OR - match if either matches (or if neither is specified)
  const hasCategoryFilter =
    filters[ActivityFilterSections.CATEGORY] &&
    filters[ActivityFilterSections.CATEGORY]!.length > 0;
  const hasSystemFilter =
    filters[ActivityFilterSections.SYSTEM] &&
    filters[ActivityFilterSections.SYSTEM]!.length > 0;

  // If no category or system filters are specified, show everything (for those filters)
  // Otherwise, match if category OR system matches
  const matchesCategoryOrSystem =
    (!hasCategoryFilter && !hasSystemFilter) ||
    (hasCategoryFilter &&
      matchesCategory(filters[ActivityFilterSections.CATEGORY]!, item)) ||
    (hasSystemFilter &&
      matchesSystemCategory(filters[ActivityFilterSections.SYSTEM]!, item));

  return matchesInput && matchesCategoryOrSystem;
};

